/**
 * Comprehensive Help Content for Lapen Holdings Rental Tracker
 * Written in plain English for non-accountant landlords with official IRS references.
 */

export const HELP_TOPICS = {
  // ===================== DASHBOARD METRICS =====================
  "noi": {
    title: "Net Operating Income (NOI)",
    category: "Financial Metrics",
    short: "Your total rental income minus all operating expenses, before mortgage payments and income taxes. A positive NOI means your properties are profitable on paper.",
    full: "Net Operating Income (NOI) measures the pure operating profitability of your rental real estate before financing costs and income taxes. It answers the fundamental question: 'Is this property generating more money than it costs to operate?' Because it ignores mortgages and depreciation, lenders and investors use NOI to evaluate the true earning power of a property.",
    steps: [
      "Calculate total operating revenue (rent, parking, pet fees, laundry).",
      "Subtract all operating expenses (maintenance, insurance, property taxes, utilities, management fees).",
      "Do NOT subtract mortgage principal or interest payments.",
      "Do NOT subtract depreciation paper deductions."
    ],
    tips: [
      "Target an NOI that leaves healthy breathing room after your monthly mortgage payment.",
      "Track NOI month-over-month to spot sudden increases in operating costs like utilities or repairs.",
      "Increasing rents by just $50/month across a portfolio dramatically boosts NOI and overall property valuation."
    ],
    irs: "IRS Publication 527 (Residential Rental Property) — Operating expenses reduce taxable net rental income on IRS Schedule E (Form 1040)."
  },

  "cash-flow": {
    title: "Monthly Cash Flow",
    category: "Financial Metrics",
    short: "Money left over in your bank account after collecting rent and paying ALL bills, including your mortgage principal and interest. Positive = money in your pocket each month.",
    full: "Cash flow is the actual physical dollars remaining in your LLC's checking account after all income is collected and every single expense — including your full mortgage payment (P&I) — has been paid. While NOI tells you how the property performs on paper, cash flow tells you if your business is putting real money in your pocket or requiring you to fund it out of pocket.",
    steps: [
      "Total Rent Collected this month.",
      "Subtract Operating Expenses (repairs, utilities, insurance, taxes).",
      "Subtract Full Mortgage Payment (both principal and interest).",
      "The remainder is your Net Monthly Cash Flow."
    ],
    tips: [
      "Always set aside at least 10% of monthly cash flow into a Capital Expenditure (CapEx) reserve fund for future big-ticket repairs like roofs and HVAC units.",
      "Negative cash flow means you are feeding the property from your personal funds, even if the property is technically building equity."
    ],
    irs: "Cash flow is an internal management metric. For tax purposes, the IRS taxes your Net Income (where only mortgage interest is deductible, not principal)."
  },

  "expense-ratio": {
    title: "Operating Expense Ratio",
    category: "Financial Metrics",
    short: "The percentage of your gross rental income consumed by operating expenses. Under 50% is generally considered healthy for residential rental properties.",
    full: "The Operating Expense Ratio (OER) compares total operating expenses to gross rental revenue. If your property brings in $3,000/month and expenses are $1,200/month, your expense ratio is 40%. A lower ratio indicates higher operational efficiency and more revenue flowing to debt service and profit.",
    steps: [
      "Sum up all monthly operating expenses (excluding mortgage debt service).",
      "Divide by total gross rental revenue collected.",
      "Multiply by 100 to get the percentage."
    ],
    tips: [
      "The classic real estate '50% Rule' estimates that half of rental income will go toward operating costs over time.",
      "If your ratio spikes over 65%, review recurring expenses: water leaks, high management fees, or unmetered tenant utilities may be draining profits."
    ],
    irs: "All ordinary and necessary operating expenses are deductible under IRC Section 162."
  },

  "portfolio-value": {
    title: "Portfolio Valuation",
    category: "Asset Tracking",
    short: "The estimated total market value of all your real estate properties combined, based on recent appraisals, tax assessments, or comparable sales (CMAs).",
    full: "Portfolio Value represents what your real estate assets would sell for on the open market today. Tracking your portfolio value allows you to monitor equity accumulation, calculate Loan-to-Value (LTV) ratios for refinancing, and determine overall net worth across all holding companies.",
    steps: [
      "Record your original purchase price when acquiring a property.",
      "Update valuations periodically using formal appraisals, refinance assessments, or comparative market analyses (CMAs).",
      "The app automatically aggregates values across properties and entities."
    ],
    tips: [
      "Update valuations once a year or whenever you complete significant capital improvements (e.g. added bedroom, new roof).",
      "Use conservative estimates — banks and lenders will always look at formal appraisal valuations."
    ],
    irs: "Market value appreciation is not taxed until you sell the property (capital gains event) or complete a Section 1031 like-kind exchange."
  },

  "equity": {
    title: "Net Real Estate Equity",
    category: "Asset Tracking",
    short: "What you actually own free and clear — your total portfolio market value minus all outstanding mortgage loan balances.",
    full: "Equity represents your actual true ownership stake in your properties. If a rental is worth $400,000 and your remaining mortgage loan balance is $250,000, your equity is $150,000. Equity grows through two engines simultaneously: property value appreciation and tenants paying down your mortgage principal each month.",
    steps: [
      "Current Property Market Value.",
      "Subtract Outstanding Loan Principal Balance.",
      "Equals your True Net Equity ($ and %)."
    ],
    tips: [
      "When your equity exceeds 25-30%, you may qualify for a Cash-Out Refinance or Home Equity Line of Credit (HELOC) to purchase your next rental property.",
      "Watching your equity percentage tick upward is the greatest indicator of long-term real estate wealth building."
    ],
    irs: "Equity itself is not taxable. Borrowing against equity via refinancing is also a tax-free event under current federal tax code."
  },

  // ===================== PROPERTIES =====================
  "leasing-type": {
    title: "Leasing Strategy (LTR vs STR vs Hybrid)",
    category: "Property Management",
    short: "LTR means long-term leases (12+ months). STR means short-term vacation rentals (Airbnb/VRBO by the night). Hybrid means flexibly switching between both.",
    full: "Different rental strategies have distinct operational requirements and tax implications:\n• Long-Term Rental (LTR): Traditional 1-year leases with steady monthly rent, passive management, and low turnover.\n• Short-Term Rental (STR): Nightly or weekly bookings through Airbnb or VRBO, generating higher gross revenue but requiring active hospitality and cleaning.\n• Hybrid / Medium-Term: 1-to-6 month rentals furnished for traveling nurses, corporate relocations, or seasonal tenants.",
    steps: [
      "Select LTR if you have long-term tenants signing a standard lease agreement.",
      "Select STR if you rent on Airbnb/VRBO with nightly rates and guest turnover.",
      "Select Hybrid if you furnish the unit and alternate between 30+ day stays and short-term bookings."
    ],
    tips: [
      "Use our built-in 'Strategy Switcher' tool to simulate which approach generates higher net annual profit for any property.",
      "Be mindful of local municipal zoning ordinances and HOA bylaws regarding minimum stay durations."
    ],
    irs: "IRS Publication 527. Average guest stay under 7 days may classify rental activity as an active business under IRC § 469 (STR loophole) rather than passive rental activity."
  },

  "purchase-price": {
    title: "Purchase Price vs Market Value",
    category: "Asset Tracking",
    short: "Purchase price is what you bought the property for. Market value is what it is worth today. The difference is your capital appreciation.",
    full: "Your purchase price sets the historical cost basis used by your accountant to calculate annual tax depreciation. Market value changes dynamically over time based on local neighborhood trends, inflation, and renovations.",
    steps: [
      "Enter the exact purchase price shown on your HUD-1 or ALTA settlement statement.",
      "Add any capital improvements made post-purchase to build your adjusted tax basis.",
      "Log appraisal updates in the Valuation History tab to monitor appreciation."
    ],
    tips: [
      "Keep closing statements and purchase contracts stored in the Document Vault forever in case of an audit."
    ],
    irs: "Form 4562 (Depreciation and Amortization) — Tax depreciation is calculated based on cost basis, NOT current market value."
  },

  "mortgage-balance": {
    title: "Mortgage Loan Balance & Amortization",
    category: "Financing",
    short: "The remaining principal amount you owe the bank right now, which decreases with every monthly mortgage payment.",
    full: "Your loan balance is different from your original loan amount. Each month, your payment is split between loan principal (which reduces your debt balance) and loan interest (the fee the bank charges). The app's built-in mortgage amortization engine calculates your exact remaining balance and projected payoff date.",
    steps: [
      "Enter your starting loan balance, interest rate, and term (years).",
      "The app generates your full 360-month amortization schedule.",
      "Use 1-click 'Post Payment' to log your monthly payment directly to your ledger."
    ],
    tips: [
      "Compare the app's calculated balance against your monthly mortgage servicer statement to ensure payments are properly applied to principal."
    ],
    irs: "Form 1098 (Mortgage Interest Statement) — Only mortgage interest is deductible on Schedule E Line 12. Principal paydown is not deductible."
  },

  "cap-rate": {
    title: "Capitalization Rate (Cap Rate)",
    category: "Financial Metrics",
    short: "Annual Net Operating Income divided by property value. A measure of investment return independent of financing. 5% to 8% is typical for residential rentals.",
    full: "The Cap Rate reveals what percentage return a property would generate if you paid 100% all-cash with no mortgage debt. It provides an apples-to-apples comparison between different properties, regardless of how much leverage or debt financing is used.",
    steps: [
      "Calculate 12 months of Net Operating Income (NOI).",
      "Divide by Current Property Value (or Purchase Price).",
      "Multiply by 100 to yield the Cap Rate percentage."
    ],
    tips: [
      "Higher cap rates usually indicate higher yield but potentially higher risk or older properties.",
      "Lower cap rates (3-4%) are common in prime coastal markets with strong long-term appreciation expectations."
    ],
    irs: "Cap Rate is an investment analysis formula; it does not appear directly on tax filings."
  },

  "land-value": {
    title: "Land Value & Depreciation Allocation",
    category: "Tax Strategy",
    short: "The portion of your purchase price attributable to the dirt/land only. Land never wears out, so the IRS does NOT allow it to be depreciated.",
    full: "When you buy real estate, you purchase both the building structure and the physical parcel of land underneath it. Because land never deteriorates, federal tax law strictly forbids depreciating land. Only the physical building structure (and land improvements like fences or paving) can be depreciated over 27.5 years. Typically, land accounts for 15% to 25% of the total purchase price depending on local county tax assessor splits.",
    steps: [
      "Check your local county property tax assessment card for the Assessed Land vs Building ratio.",
      "Apply that percentage to your actual purchase price.",
      "Enter the land value in your property setup. The remainder is your depreciable building basis."
    ],
    tips: [
      "Cost segregation studies accurately pinpoint the exact legal land allocation to maximize your depreciable building basis.",
      "Never claim 100% of purchase price as building basis — this is an automatic IRS audit red flag."
    ],
    irs: "IRS Publication 527 Chapter 2: 'You can never depreciate the cost of land because land does not wear out, become obsolete, or get used up.'"
  },

  // ===================== TRANSACTIONS & SCHEDULE E =====================
  "schedule-e": {
    title: "IRS Schedule E (Supplemental Income and Loss)",
    category: "IRS Tax Rules",
    short: "Schedule E is the official IRS tax form landlords use to report rental income and deductible expenses. Every transaction you record maps to a Schedule E line item.",
    full: "Schedule E (Part I) is where individual landlords and single-member LLCs report rental real estate activity on their Form 1040. Every dollar spent on your rental properties should be assigned to an official Schedule E category (like Repairs, Supplies, Insurance, or Taxes). At tax time, our 1-Click Accountant Package generates a completed Schedule E summary that your CPA can copy directly into their tax software.",
    steps: [
      "Every transaction in the Books is classified as Income or Expense.",
      "Each expense is assigned one of the 15 IRS Schedule E categories.",
      "Generate the 1-Click Accountant Package in Settings at year-end for your tax preparer."
    ],
    tips: [
      "Always attach receipt photos or PDF invoices for any expense over $75 for audit defense.",
      "Separating repairs (deductible immediately) from capital improvements (depreciated over time) saves thousands."
    ],
    irs: "IRS Form 1040 Schedule E — Supplemental Income and Loss from Rental Real Estate."
  },

  "income-types": {
    title: "Rental Income Reporting",
    category: "IRS Tax Rules",
    short: "Rent payments, late fees, pet rent, and cleaning fees collected from tenants all count as taxable rental income and must be reported.",
    full: "The IRS defines rental income as any payment received for the use or occupation of property. In addition to regular monthly rent, you must report advance rent, non-refundable pet fees, parking fees, and tenant-paid utility reimbursements.",
    steps: [
      "Record monthly rent payments under 'Rent' category.",
      "Record Airbnb/VRBO gross payout under 'STR Booking'.",
      "Do NOT record security deposits as income unless you retain them for unpaid rent or damages."
    ],
    tips: [
      "Security deposits are a liability (money you hold for the tenant), not income, until legally applied to repairs or unpaid rent at move-out."
    ],
    irs: "IRS Pub 527 Chapter 1: Rental Income must be reported in the year received, regardless of when it was earned (cash method of accounting)."
  },

  "capital-contribution": {
    title: "Capital Contribution",
    category: "LLC Accounting",
    short: "Money you personally put into your LLC checking account from your personal funds. This is NOT income — it is an owner investment.",
    full: "A Capital Contribution occurs when an owner deposits personal funds into the business bank account to cover down payments, renovations, or cash shortfalls. Because this is equity you injected into the business, it is NOT taxable income to the LLC and does not appear on Schedule E. Recording it accurately establishes your member capital balance.",
    steps: [
      "Transfer funds from personal bank to LLC business checking.",
      "Record transaction type 'Income' with category 'Capital Contribution'.",
      "This increases your LLC Member Capital Account balance cleanly."
    ],
    tips: [
      "Never co-mingle personal and business funds. Always deposit money into the LLC account first, then pay expenses from the LLC.",
      "Accurate records allow you to withdraw your contributed capital later tax-free via Owner Draws."
    ],
    irs: "IRC Section 721 — Non-recognition of gain or loss on contribution of property/cash to a partnership or LLC."
  },

  "owner-draw": {
    title: "Owner Draw (Distribution)",
    category: "LLC Accounting",
    short: "Money you take out of the LLC business account for personal use. This is NOT a business expense — it is a distribution of profits.",
    full: "When your rental properties generate surplus cash, you can distribute money to yourself as an Owner Draw. An owner draw is not a deductible business expense, so it does NOT reduce your taxable rental profit. It simply reduces your cash in the bank and lowers your Member Capital balance.",
    steps: [
      "Transfer surplus profit from LLC business checking to personal account.",
      "Record transaction type 'Expense' with category 'Owner Draw'.",
      "This reduces your LLC Capital Account balance without distorting your Schedule E expenses."
    ],
    tips: [
      "Always leave an adequate operating buffer (typically 3–6 months of expenses) before taking an owner draw.",
      "In a multi-member LLC, owner draws should follow the ownership percentage specified in your Operating Agreement."
    ],
    irs: "IRS Publication 541 — Distributions from an LLC are generally not taxable unless they exceed the member's tax basis in the LLC."
  },

  "due-to-member": {
    title: "Due to Member (Reimbursement)",
    category: "LLC Accounting",
    short: "Money the LLC owes you because you paid for a business expense out of your personal pocket. Record these so the LLC can reimburse you cleanly.",
    full: "Sometimes in an emergency you might swipe your personal credit card at the hardware store or pay a contractor with personal cash. Recording this as 'Due to Member' creates a formal liability on the LLC's books showing the company owes you reimbursement, preserving corporate veil protection.",
    steps: [
      "Record the expense under its correct Schedule E category (e.g. Repairs) with payment method 'Due to Member'.",
      "When the LLC has funds, write a check or transfer from LLC to your personal account to repay yourself.",
      "Attach the store receipt to the transaction for audit compliance."
    ],
    tips: [
      "Reimburse yourself within the same tax year to keep the corporate balance sheet clean and protect limited liability status."
    ],
    irs: "Accountable Plan under IRC § 62(a)(2)(A) — Reimbursements for legitimate business expenses are non-taxable to the owner."
  },

  // ===================== SCHEDULE E CATEGORIES =====================
  "cat-advertising": {
    title: "Advertising (Schedule E Line 5)",
    category: "Schedule E Categories",
    short: "Costs to market your rental and find tenants — Zillow listings, yard signs, Craigslist ads, professional real estate photography, and marketing flyers.",
    full: "All direct costs incurred to advertise vacancies, attract quality tenants, and promote your rental property are fully deductible in the year paid. This includes paid listing syndication (Zillow Premier, Apartments.com), social media ads, drone/listing photography, and physical 'For Rent' banners.",
    steps: ["Assign receipt to 'Advertising'.", "Link to the specific vacant property."],
    tips: ["Professional photos cost $150-$300 but cut vacancy time in half and are 100% tax-deductible."],
    irs: "Schedule E Line 5: Advertising."
  },

  "cat-auto-travel": {
    title: "Auto & Travel (Schedule E Line 6)",
    category: "Schedule E Categories",
    short: "Miles driven for rental purposes — showing properties, hardware store runs, meeting contractors, and inspecting units. Use the Mileage Tracker to log trips.",
    full: "You can deduct vehicular travel expenses for managing, conserving, or maintaining your rental properties. The IRS allows you to choose between the standard mileage rate ($0.70/mile in 2025) or actual vehicle expenses. Our built-in Mileage Tracker keeps the required contemporaneous travel log.",
    steps: ["Log date, destination, business purpose, and odometer/miles.", "The app calculates your total Schedule E deduction."],
    tips: ["Commuting between your primary home and regular W-2 job does NOT qualify. Trips from home to rental property DO qualify if you have a qualified home office."],
    irs: "Schedule E Line 6: Auto and travel. Requires contemporaneous written records per IRC § 274(d)."
  },

  "cat-cleaning-maintenance": {
    title: "Cleaning & Maintenance (Schedule E Line 7)",
    category: "Schedule E Categories",
    short: "Regular cleaning, lawn care, pest control, pool maintenance, and routine servicing that keeps your property clean and tenant-ready.",
    full: "Covers recurring routine upkeep tasks that maintain the property in ordinary efficient operating condition. This differs from capital improvements because maintenance simply preserves existing value rather than adding substantial new value.",
    steps: ["Assign turnover cleaning, landscaping, snow removal, and gutter cleaning here."],
    tips: ["Keep vendor invoices from lawn and cleaning services with property address clearly identified."],
    irs: "Schedule E Line 7: Cleaning and maintenance."
  },

  "cat-commissions": {
    title: "Commissions (Schedule E Line 8)",
    category: "Schedule E Categories",
    short: "Fees paid to real estate agents, leasing brokers, or placement agencies for finding and vetting tenants.",
    full: "When a licensed real estate agent or leasing service places a tenant in your rental and charges a fee (typically half to one full month's rent), that commission is deductible under Line 8.",
    steps: ["Record leasing agent placement fee after lease execution."],
    tips: ["Platform fees from Airbnb or VRBO are best tracked under Platform Fees or Line 19 Other."],
    irs: "Schedule E Line 8: Commissions."
  },

  "cat-insurance": {
    title: "Insurance Premiums (Schedule E Line 9)",
    category: "Schedule E Categories",
    short: "Landlord dwelling fire, liability, flood, and umbrella insurance premiums. NOT your personal primary homeowner policy.",
    full: "Premiums paid for hazard, liability, flood, windstorm, loss-of-rent, or umbrella insurance covering your rental property are deductible. If you pay insurance through mortgage escrow, deduct only what was actually disbursed by the escrow agent during the tax year.",
    steps: ["Record annual or monthly landlord insurance policy premiums."],
    tips: ["Always ensure your policy is written as a 'Dwelling Fire / Landlord' policy (DP-3), not a homeowner policy (HO-3)."],
    irs: "Schedule E Line 9: Insurance."
  },

  "cat-legal-professional": {
    title: "Legal & Professional Fees (Schedule E Line 10)",
    category: "Schedule E Categories",
    short: "Attorney fees for leases or evictions, CPA tax preparation fees, bookkeeping costs, and real estate tax consulting.",
    full: "Covers fees paid to attorneys, accountants, bookkeepers, and tax professionals for services directly related to operating and maintaining your rental properties.",
    steps: ["Assign CPA fees, tenant lease drafting, or eviction attorney costs here."],
    tips: ["Legal fees for acquiring a property (e.g. closing attorney) must be added to purchase basis, not expensed here."],
    irs: "Schedule E Line 10: Legal and other professional fees."
  },

  "cat-management-fees": {
    title: "Management Fees (Schedule E Line 11)",
    category: "Schedule E Categories",
    short: "Monthly percentages or flat fees paid to a third-party property management company.",
    full: "If you hire a professional property manager to collect rent, handle tenant communications, and coordinate repairs, their management fee (typically 8%–10% of monthly collected rent) is fully deductible on Line 11.",
    steps: ["Record monthly management deductions shown on your property manager statement."],
    tips: ["Ensure the property manager provides an annual Form 1099-MISC showing total rent and fees."],
    irs: "Schedule E Line 11: Management fees."
  },

  "cat-mortgage-interest": {
    title: "Mortgage Interest (Schedule E Line 12)",
    category: "Schedule E Categories",
    short: "ONLY the interest portion of your loan payments is deductible. Principal paydown is equity, not a tax deduction. Refer to Form 1098.",
    full: "Interest paid to banks or private lenders on loans secured by your rental property is one of the largest tax deductions for real estate investors. In January, your lender sends Form 1098 showing the exact interest paid during the previous calendar year.",
    steps: ["Record the interest split from your monthly mortgage payment or use Form 1098 at year end."],
    tips: ["Use our Mortgage Tracker feature to automatically break down principal vs interest for every monthly payment."],
    irs: "Schedule E Line 12: Mortgage interest paid to banks (supported by Form 1098)."
  },

  "cat-repairs": {
    title: "Repairs (Schedule E Line 14)",
    category: "Schedule E Categories",
    short: "Fixes that restore property to its existing condition — patching a roof leak, unclogging plumbing, repairing broken windows. Fully deductible in Year 1.",
    full: "Under IRS Tangible Property Regulations, a repair keeps your property in normal, efficient operating condition without materially adding to its value or substantially prolonging its useful life. Repairs are 100% deductible in the year paid, unlike capital improvements which must be depreciated over 27.5 years.",
    steps: ["Link contractor repair bills to maintenance requests and categorize under Repairs."],
    tips: ["De Minimis Safe Harbor rule (Treas. Reg. § 1.263(a)-1(f)) allows you to immediately expense items up to $2,500 each even if they might normally be capitalized."],
    irs: "Schedule E Line 14: Repairs. See IRS Tangible Property Regulations (TD 9636)."
  },

  "cat-supplies": {
    title: "Supplies (Schedule E Line 15)",
    category: "Schedule E Categories",
    short: "Small items consumed in operating the property — lightbulbs, smoke detector batteries, air filters, door locks, and cleaning chemicals.",
    full: "Covers expendable supplies used directly in your rental operations. This includes replacement HVAC filters, smart lock batteries, touch-up paint, and consumables supplied to tenants.",
    steps: ["Scan Home Depot, Lowe's, or hardware receipts with our AI Scanner and assign to Supplies."],
    tips: ["Consistently replacing furnace filters saves thousands in HVAC repairs and is fully deductible."],
    irs: "Schedule E Line 15: Supplies."
  },

  "cat-taxes": {
    title: "Taxes (Schedule E Line 16)",
    category: "Schedule E Categories",
    short: "Real estate property taxes paid to your county or city government. Does NOT include your personal income taxes.",
    full: "Local county, municipal, and school district property taxes assessed against the rental parcel are deductible. If property taxes are paid via an escrow account, deduct only the amount the bank actually paid to the taxing authority during the tax year.",
    steps: ["Record property tax bills or escrow annual summary statement."],
    tips: ["Check your county tax bill for special assessments; certain assessments for local improvements (like paving a new street) must be added to land basis."],
    irs: "Schedule E Line 16: Taxes."
  },

  "cat-utilities": {
    title: "Utilities (Schedule E Line 17)",
    category: "Schedule E Categories",
    short: "Water, sewer, gas, electricity, trash, and internet — ONLY if YOU (the landlord) pay them, not your tenant.",
    full: "You can deduct utility costs you pay directly for the rental property. Common examples include water/sewer bills paid by landlord, electricity during tenant vacancies, trash collection, and Wi-Fi provided in short-term rentals.",
    steps: ["Record municipal utility bills paid directly from your business checking account."],
    tips: ["If a tenant reimburses you for utilities, record the bill as an expense and the reimbursement as income."],
    irs: "Schedule E Line 17: Utilities."
  },

  "cat-depreciation": {
    title: "Depreciation (Schedule E Line 18)",
    category: "Schedule E Categories",
    short: "The IRS allows you to deduct the cost of the building (excluding land) spread over 27.5 years. This 'paper loss' shelters rental income from taxes.",
    full: "Depreciation is the crown jewel of rental real estate tax benefits. The IRS allows residential landlords to deduct 1/27.5th (approx 3.636%) of the building's cost basis each year as an allowance for wear and tear, even while the property actually appreciates in market value! This non-cash deduction frequently turns cash-flow positive rentals into paper tax losses.",
    steps: [
      "Determine Building Cost Basis (Purchase price + closing costs − Land value).",
      "Divide by 27.5 years to get annual straight-line depreciation.",
      "Record the deduction annually on Schedule E Line 18 via Form 4562."
    ],
    tips: [
      "Use our Cost Segregation tool to accelerate portions of depreciation into Year 1 for massive upfront tax savings.",
      "Depreciation is mandatory — even if you forget to claim it, the IRS will calculate depreciation recapture tax when you sell!"
    ],
    irs: "Schedule E Line 18 & Form 4562. Modified Accelerated Cost Recovery System (MACRS) 27.5-year residential recovery period under IRC § 168."
  },

  // ===================== FEATURES & TOOLS =====================
  "mileage-tracker": {
    title: "IRS Business Mileage Tracker",
    category: "Tax Compliance",
    short: "Deduct business miles driven for your rentals at $0.70/mile (2025 IRS rate). The IRS requires contemporaneous records of date, destination, and purpose.",
    full: "Driving to your rental properties for inspections, meeting contractors, showing vacancies, or purchasing supplies at the hardware store is 100% tax-deductible. The IRS strictly enforces the 'contemporaneous log' rule: you must record trips around the time they happen, not reconstruct them months later. This tool automatically calculates your dollar deduction based on current IRS rates.",
    steps: [
      "Click 'Log Trip' whenever you drive for rental purposes.",
      "Enter trip date, miles driven, property destination, and specific business purpose.",
      "The app computes your dollar deduction and includes it in your year-end Schedule E summary."
    ],
    tips: [
      "Qualifying purposes: collecting rent, meeting repair contractors, buying supplies, property maintenance, meeting your CPA.",
      "Non-qualifying trips: your regular commute to a W-2 job or personal detours."
    ],
    irs: "IRC Section 274(d) Substantiation Requirements; IRS Notice 2024-08 Standard Mileage Rates."
  },

  "receipt-scanner": {
    title: "AI Receipt Scanner (Gemini OCR)",
    category: "Productivity",
    short: "Snap a photo of any receipt or invoice to automatically extract vendor, amount, date, and line items using Google Gemini Flash AI.",
    full: "Say goodbye to manual data entry. Our AI scanner uses Google Gemini Vision to read printed store receipts, contractor invoices, and PDF bills. It automatically detects the vendor name, payment date, total dollar amount, suggests the proper IRS Schedule E category, and parses individual line items so you can exclude personal purchases.",
    steps: [
      "Take a clear, well-lit photo of your receipt or upload a PDF invoice.",
      "Review the extracted fields: Amount, Date, Vendor, and Category.",
      "Toggle 'Itemize Receipt' if you bought personal items on the same receipt that should not be deducted.",
      "Save the transaction. The receipt image is permanently archived in `/data/uploads/` for IRS audit defense."
    ],
    tips: [
      "Lay receipts flat on a dark background for maximum OCR accuracy.",
      "Ensure all four corners and the total amount line are visible in the camera frame."
    ],
    irs: "Revenue Procedure 98-25 — Electronic recordkeeping standards for tax audit defense."
  },

  "cost-segregation": {
    title: "Cost Segregation & Bonus Depreciation",
    category: "Advanced Tax Strategy",
    short: "Accelerate depreciation on 5-year personal property (appliances, carpet) and 15-year land improvements (fences, paving) rather than waiting 27.5 years.",
    full: "Cost segregation is an IRS-approved engineering and accounting method that dissects a property's purchase price into shorter-lived asset classes. Instead of depreciating the entire building over 27.5 years, components like carpeting, appliances, specialty electrical (5-year) and sidewalks, fencing, and landscaping (15-year) are depreciated faster. Under bonus depreciation rules, eligible assets can be deducted in the very first year.",
    steps: [
      "View estimated cost seg breakdown under Operations -> Cost Segregation.",
      "Examine 5-year, 15-year, and 27.5-year asset distributions.",
      "Review available first-year bonus depreciation estimates to shelter active or passive rental income."
    ],
    tips: [
      "Cost segregation provides substantial benefits for properties valued over $500,000.",
      "Always have your CPA or a qualified cost segregation engineer certify study allocations before filing Form 3115 or Form 4562."
    ],
    irs: "IRS Cost Segregation Audit Techniques Guide (ATG) & IRC Section 168(k) Bonus Depreciation."
  },

  "rent-roll": {
    title: "Rent Roll & Tenant Ledger",
    category: "Operations",
    short: "A complete snapshot of all tenant leases, monthly rent rates, payment status, and lease expiration dates required by banks, lenders, and buyers.",
    full: "A rent roll is the universal scorecard for rental properties. Commercial lenders require a current rent roll when refinancing or issuing loans, and prospective buyers demand it during due diligence. Our rent roll automatically tracks overdue statuses and provides 1-click PDF Rent Receipt generation for tenants.",
    steps: [
      "Link tenants to properties and enter monthly rent and due dates.",
      "The rent roll automatically flags overdue payments if no income is recorded in the current billing cycle.",
      "Click 'Receipt' on any tenant row to generate an official PDF payment receipt."
    ],
    tips: [
      "Record rent payments promptly when received to ensure overdue badges remain accurate."
    ],
    irs: "Bank underwriting standard document (Fannie Mae Form 1007 / Freddie Mac Form 1000)."
  },

  "recurring-transactions": {
    title: "Recurring Transactions & Manual Approval",
    category: "Automation",
    short: "Schedule repeating expenses like monthly mortgages, quarterly insurance, or annual property taxes with a 1-click manual approval guardrail.",
    full: "Recurring transactions eliminate repetitive data entry for predictable bills. Instead of creating a new transaction every month for lawn care or mortgage payments, you set up a schedule. When a payment is due, it appears in your Dashboard Upcoming Widget for 1-click review and approval.",
    steps: [
      "Set up recurring rule: Property, Category, Amount, Frequency (Monthly, Quarterly, Annual), and Next Due Date.",
      "Review upcoming due items on your Dashboard.",
      "Click 'Approve' to instantly post the transaction to your official books."
    ],
    tips: [
      "We deliberately require 1-click manual approval rather than silent auto-posting so you maintain complete awareness and control over your financial ledger."
    ],
    irs: "Ensures no duplicate deductions are posted to your Schedule E ledger."
  },

  "capital-account": {
    title: "LLC Capital Account & Member Equity",
    category: "LLC Accounting",
    short: "Tracks your ownership equity balance in the LLC — measuring capital you contributed, profits earned, and personal distributions taken.",
    full: "A capital account represents an owner's equity book value in an LLC. It starts with your initial capital contribution, increases with your share of net rental profits, and decreases with every owner draw or distribution you take. Maintaining an accurate capital account is essential for multi-member partnerships and proving corporate solvency to lenders.",
    steps: [
      "Beginning Capital Balance.",
      "+ Plus Capital Contributions (personal money deposited).",
      "+ Plus Net Rental Profits.",
      "− Minus Owner Draws (cash distributions taken out).",
      "= Ending Member Capital Balance."
    ],
    tips: [
      "Never allow your capital account to go negative in a partnership without consulting your CPA, as negative basis can trigger unexpected capital gains tax."
    ],
    irs: "IRS Form 1065 Schedule K-1 (Partner's Share of Income, Deductions, Credits) Item L."
  },

  "maintenance-tracker": {
    title: "Maintenance Work Orders & Habitability",
    category: "Operations",
    short: "Track property repair requests, assign urgency priority, upload damage photos, and link contractor costs directly to deductible Schedule E repairs.",
    full: "Prompt maintenance keeps tenants happy, protects asset value, and ensures compliance with state landlord-tenant habitability laws. Our tracker organizes work orders from Open to Resolved, lets you attach contractor repair invoices, and features a 1-click 'Resolve & Link' modal that logs the repair cost into your books automatically.",
    steps: [
      "Log maintenance ticket with property, description, and priority level.",
      "Priority levels: Low (cosmetic), Medium (normal repair), High (appliance/heating issue), Urgent (flood, gas leak, electrical hazard).",
      "Attach photos taken before and after the repair.",
      "Click 'Resolve & Link' to close the work order and record the repair invoice in your ledger."
    ],
    tips: [
      "Urgent issues have pulsing red indicators — address emergencies immediately to prevent catastrophic water or structural damage.",
      "Documenting maintenance with photos provides bulletproof evidence in tenant security deposit disputes."
    ],
    irs: "Repairs linked from work orders map directly to Schedule E Line 14."
  },

  "mortgage-tracker": {
    title: "Mortgage Payment Tracker & Amortization",
    category: "Financing",
    short: "Track loan balance, equity growth, monthly P&I breakdown, and annual amortization schedules with 1-click payment posting.",
    full: "Each property detail page includes a dedicated Mortgage & Loan tab powered by a full financial amortization engine. It calculates your exact remaining loan balance, estimated net equity, monthly payment split, and full 30-year amortization schedule showing principal vs interest breakdown.",
    steps: [
      "Enter loan balance, interest rate, term, and origination date in property settings.",
      "View your annual amortization schedule showing beginning balance, principal paid, interest paid, and ending balance.",
      "Click 'Post Payment Now' to log this month's mortgage payment with principal and interest notes."
    ],
    tips: [
      "Making just one extra principal payment per year on a 30-year mortgage can shave off 5 to 7 years of debt!"
    ],
    irs: "Form 1098 — Mortgage interest is deductible; principal payments increase your balance sheet equity."
  },

  "document-vault": {
    title: "Encrypted Document Vault",
    category: "Compliance & Security",
    short: "Securely store PDF leases, deeds, insurance policies, and inspection reports on your Synology NAS with permanent audit protection.",
    full: "Never hunt through paper filing cabinets again. The Document Vault provides a central repository on your Synology NAS (`/data/documents/`) for legal and operational records. Files up to 30MB each are categorized by document type, equipped with inline previews, and tracked with a real-time storage indicator.",
    steps: [
      "Upload leases, closing statements, warranties, deeds, and insurance policies.",
      "Filter by category: Lease Agreement, Insurance Policy, Deed & Title, Property Inspection, Mortgage & Loan, Tax Document.",
      "Preview PDFs directly in your browser or download anytime."
    ],
    tips: [
      "The IRS can audit rental tax returns up to 3 years after filing (or 6 years if income is underreported by 25%). Keep closing packets and tax returns indefinitely."
    ],
    irs: "IRS Publication 583 (Starting a Business and Keeping Records) — Record retention guidelines."
  },

  "email-notifications": {
    title: "Automated Email Notifications (SMTP)",
    category: "System Settings",
    short: "Configure your own private email server (Gmail, Outlook, or NAS) to send automatic rent reminders, overdue alerts, and contractor work orders.",
    full: "Keep tenants and contractors updated automatically without paying third-party email service fees. Using industry-standard SMTP (Simple Mail Transfer Protocol), notifications are dispatched directly from your own email account in a background thread.",
    steps: [
      "Go to Settings -> Email Notifications & SMTP Server.",
      "Enter your SMTP host (e.g. `smtp.gmail.com`), port (587), username, and app password.",
      "Enable notification toggles: Rent Due, Overdue Rent, Lease Expiration, Maintenance Orders.",
      "Use 'Send Test Email' to verify delivery, and review live delivery logs."
    ],
    tips: [
      "For Gmail: Enable 2-Step Verification in your Google Account and generate an 'App Password'. Do not use your primary personal password.",
      "All emails are sent directly through your private credentials — no tenant or financial data ever touches third-party relay servers."
    ],
    irs: "Written email notices provide legal documentation of rent demands and lease communications."
  },

  "multi-llc": {
    title: "Multi-LLC & Legal Entity Segregation",
    category: "Asset Protection",
    short: "Manage multiple LLC holding companies under one roof with independent books, segregated tax packages, and global entity switching.",
    full: "Holding rental properties in distinct Limited Liability Companies (LLCs) isolates liabilities. If an accident or lawsuit occurs at Property A, the assets and equity in Property B (owned by a separate LLC) are shielded from creditor claims. Our global entity switcher allows you to view unified portfolio KPIs or filter down to a single legal entity.",
    steps: [
      "Create your legal entities in Settings -> Legal Entities & LLC Management.",
      "Assign each property to its owning LLC.",
      "Use the Header Entity Switcher to filter your Dashboard, Ledger, and Reports by entity.",
      "Download independent 1-Click Accountant Packages customized for each LLC."
    ],
    tips: [
      "Maintain separate business bank accounts for each LLC to prevent 'piercing the corporate veil'.",
      "Each multi-member LLC typically files its own separate Form 1065 partnership tax return."
    ],
    irs: "Entity Classification under Treasury Regulation § 301.7701-3 (Check-the-Box Regulations)."
  }
};
